/**
 * My progress — the member's own side of the Progress Monitor.
 *
 * Deliberately narrower than the staff view: the member sees their own
 * readings, their own targets and how consistently they have shown up, and
 * can self-log only the metrics the studio flagged as safe to self-record.
 * The member id is never sent — the backend takes it from the token.
 *
 * No charting library here: the portal is a PWA people open on a phone, and
 * a hand-rolled sparkline costs a few lines instead of a few hundred KB.
 */
import { useState } from 'react';
import {
  Activity, CalendarCheck, Flame, Minus, Plus, Ruler, Target, TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { memberPortal, useAsync } from '@fitboxx/shared';
import type {
  GoalBlock, MetricBlock, MetricDefinition, ProgressSummary,
} from '@fitboxx/shared';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

function fmtDate(s?: string | null) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(undefined, { dateStyle: 'medium' }); }
  catch { return s; }
}

function num(v: unknown, dp = 1): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(dp) : String(v);
}

/** A line through the readings. Normalised to the series' own range so a
 *  2 kg swing is as readable as a 40 kg one. */
function Sparkline({ points, dp }: { points: { t: number; v: number }[]; dp: number }) {
  if (points.length < 2) return null;
  const W = 300, H = 64, PAD = 4;
  const xs = points.map((p) => p.t);
  const ys = points.map((p) => p.v);
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const y0 = Math.min(...ys), y1 = Math.max(...ys);
  const xSpan = x1 - x0 || 1;
  // A dead-flat series would divide by zero and collapse onto an edge —
  // give it a band so the line sits in the middle instead.
  const ySpan = y1 - y0 || Math.max(Math.abs(y1), 1) * 0.1;
  const px = (t: number) => PAD + ((t - x0) / xSpan) * (W - PAD * 2);
  const py = (v: number) => H - PAD - ((v - y0) / ySpan) * (H - PAD * 2);
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${px(p.t).toFixed(1)},${py(p.v).toFixed(1)}`).join(' ');
  const area = `${d} L${px(x1).toFixed(1)},${H} L${px(x0).toFixed(1)},${H} Z`;
  const last = points[points.length - 1];

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-16" preserveAspectRatio="none"
        role="img" aria-label={`${points.length} readings, latest ${num(last.v, dp)}`}>
        <path d={area} fill="hsl(var(--primary))" fillOpacity={0.08} />
        <path d={d} fill="none" stroke="hsl(var(--primary))" strokeWidth={2}
          vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={px(last.t)} cy={py(last.v)} r={3} fill="hsl(var(--primary))"
          vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
        <span>{fmtDate(new Date(x0).toISOString())}</span>
        <span>{fmtDate(new Date(x1).toISOString())}</span>
      </div>
    </div>
  );
}

function Delta({
  value, unit, direction, dp,
}: {
  value?: string | number | null; unit?: string;
  direction?: MetricDefinition['direction']; dp: number;
}) {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) {
    return (
      <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
        <Minus className="h-3 w-3" /> no change
      </span>
    );
  }
  const good = direction === 'up' ? n > 0 : direction === 'down' ? n < 0 : null;
  const tone = good === null ? 'text-muted-foreground'
    : good ? 'text-emerald-600' : 'text-amber-600';
  const Icon = n > 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`text-xs font-medium inline-flex items-center gap-1 tabular-nums ${tone}`}>
      <Icon className="h-3 w-3" />{n > 0 ? '+' : '−'}{Math.abs(n).toFixed(dp)} {unit}
    </span>
  );
}

function GoalCard({ goal }: { goal: GoalBlock }) {
  const m = goal.metric;
  const pct = goal.progress_pct == null ? null : Number(goal.progress_pct);
  const tone = pct == null ? 'bg-muted-foreground/30'
    : pct >= 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-primary' : 'bg-amber-500';
  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm">{m.label}</div>
          <div className="text-xs text-muted-foreground tabular-nums mt-0.5">
            {num(goal.start_value, m.decimal_places)} → {num(goal.target_value, m.decimal_places)} {m.unit}
            {goal.target_date && ` · by ${fmtDate(goal.target_date)}`}
          </div>
        </div>
        {goal.status === 'achieved'
          ? <Badge variant="success">achieved</Badge>
          : goal.reached
            ? <Badge variant="success">target met</Badge>
            : goal.days_remaining != null && goal.days_remaining < 0
              ? <Badge variant="warning">past the date</Badge>
              : null}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
          <div className={`h-full rounded-full ${tone}`}
            style={{ width: `${pct == null ? 0 : Math.min(100, Math.max(0, pct))}%` }} />
        </div>
        <span className="text-xs tabular-nums text-muted-foreground w-9 text-right">
          {pct == null ? '—' : `${pct.toFixed(0)}%`}
        </span>
      </div>
    </Card>
  );
}

function MetricCard({ block }: { block: MetricBlock }) {
  const m = block.metric;
  const dp = m.decimal_places ?? 1;
  const points = block.series.map((p) => ({ t: new Date(p.t).getTime(), v: Number(p.v) }));
  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm">{m.label}</div>
          <div className="text-xs text-muted-foreground">
            {block.count} reading{block.count === 1 ? '' : 's'} · latest {fmtDate(block.latest.recorded_at)}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-semibold tabular-nums leading-none">
            {num(block.latest.value, dp)}
            <span className="text-xs font-normal text-muted-foreground ml-1">{m.unit}</span>
          </div>
          <div className="mt-1">
            <Delta value={block.change} unit={m.unit} direction={m.direction} dp={dp} />
          </div>
        </div>
      </div>
      {points.length >= 2 && <div className="mt-3"><Sparkline points={points} dp={dp} /></div>}
      {block.total_change != null && (
        <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
          since {fmtDate(block.baseline_at)}:
          <Delta value={block.total_change} unit={m.unit} direction={m.direction} dp={dp} />
        </div>
      )}
    </Card>
  );
}

function LogDialog({
  open, onOpenChange, onSaved,
}: { open: boolean; onOpenChange: (o: boolean) => void; onSaved: () => void }) {
  const { data } = useAsync(
    () => memberPortal.progressMetrics() as Promise<MetricDefinition[]>,
    [],
  );
  const metrics = data ?? [];
  const [metricId, setMetricId] = useState('');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const chosen = metrics.find((m) => m.id === metricId);

  async function save() {
    setSaving(true); setErr(null);
    try {
      await memberPortal.logMeasurement({ metric: metricId, value });
      setValue(''); setMetricId('');
      onSaved();
      onOpenChange(false);
    } catch (e) {
      setErr((e as Error).message || 'Could not save that reading.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Log a reading</DialogTitle>
          <DialogDescription>
            {metrics.length
              ? 'Your studio decides what you can record yourself.'
              : 'Your studio has not opened any measurement for self-recording yet.'}
          </DialogDescription>
        </DialogHeader>
        {metrics.length > 0 && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="metric">What are you recording?</Label>
              <select
                id="metric"
                value={metricId}
                onChange={(e) => { setMetricId(e.target.value); setErr(null); }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Pick one…</option>
                {metrics.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}{m.unit ? ` (${m.unit})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="value">Value{chosen?.unit ? ` (${chosen.unit})` : ''}</Label>
              <Input
                id="value" type="number" inputMode="decimal" value={value}
                min={chosen?.min_value == null ? undefined : Number(chosen.min_value)}
                max={chosen?.max_value == null ? undefined : Number(chosen.max_value)}
                onChange={(e) => setValue(e.target.value)}
              />
              {chosen?.description && (
                <p className="text-xs text-muted-foreground">{chosen.description}</p>
              )}
            </div>
            {err && <p className="text-sm text-destructive">{err}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => void save()} disabled={saving || !metricId || value === ''}>
            {saving ? 'Saving…' : 'Save reading'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Progress() {
  const [logging, setLogging] = useState(false);
  const { data, loading, refresh } = useAsync(
    () => memberPortal.progress(365) as Promise<ProgressSummary>,
    [],
  );

  if (loading && !data) return <Skeleton className="h-64 w-full" />;
  if (!data) return null;

  const { attendance, context } = data;
  const goals = data.goals.filter((g) => g.status === 'active' || g.status === 'achieved');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold tracking-tight">My progress</h1>
        <Button size="sm" className="ml-auto" onClick={() => setLogging(true)}>
          <Plus className="h-4 w-4" /> Log
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Card className="p-3">
          <div className="text-2xl font-semibold tabular-nums leading-none">
            {attendance.attended_last_30}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
            <CalendarCheck className="h-3 w-3" /> visits / 30d
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-2xl font-semibold tabular-nums leading-none">
            {attendance.streak_weeks}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
            <Flame className="h-3 w-3" /> week streak
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-2xl font-semibold tabular-nums leading-none">
            {context.bmi == null ? '—' : num(context.bmi, 1)}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
            <Activity className="h-3 w-3" /> BMI
          </div>
        </Card>
      </div>

      {goals.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <Target className="h-3.5 w-3.5" /> My targets
          </h2>
          {goals.map((g) => <GoalCard key={g.id} goal={g} />)}
        </div>
      )}

      <div className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          My readings
        </h2>
        {!context.has_any_reading ? (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            <Ruler className="h-6 w-6 mx-auto mb-2 text-muted-foreground/60" />
            Nothing measured yet. Your first reading is the baseline everything
            afterwards is compared against — log one, or ask at the desk.
            <div className="mt-3">
              <Button size="sm" variant="outline" onClick={() => setLogging(true)}>
                Log a reading
              </Button>
            </div>
          </Card>
        ) : (
          data.metrics.map((b) => <MetricCard key={b.metric.id} block={b} />)
        )}
      </div>

      <LogDialog open={logging} onOpenChange={setLogging} onSaved={refresh} />
    </div>
  );
}
